import React, { useCallback, useEffect, useMemo, useState } from 'react';

// Phase 2B of the practitioner portal.
// Renders an AI-generated naturopathic interpretation of a stored case.
// Practitioner-facing only — no client-facing softening.

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3002';

interface InterpretationMeta {
  id: string;
  case_id: string;
  generated_by_user_id: string;
  model_used: string | null;
  generation_status: 'ok' | 'error' | 'partial';
  error_message: string | null;
  prompt_tokens: number | null;
  completion_tokens: number | null;
  total_tokens: number | null;
  generation_seconds: number | null;
  created_at: string;
}

interface InterpretationFull extends InterpretationMeta {
  interpretationText: string | null;
  interpretationData: Record<string, string> | null;
  qdrantSources: any[] | null;
}

interface PanelProps {
  caseId: string | null;
}

async function apiFetch(path: string, options: RequestInit = {}) {
  const token = localStorage.getItem('auth_token');
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {})
    }
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`${res.status}: ${text || res.statusText}`);
  }
  return res.json();
}

const formatDate = (iso: string | null | undefined) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  } catch { return iso; }
};

export const CaseInterpretationPanel: React.FC<PanelProps> = ({ caseId }) => {
  const [latest, setLatest] = useState<InterpretationFull | null>(null);
  const [history, setHistory] = useState<InterpretationMeta[]>([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewingId, setViewingId] = useState<string | null>(null);

  const fetchLatest = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch(`/api/practitioner-cases/${id}/interpretations/latest`);
      setLatest(data.interpretation || null);
      setViewingId(data.interpretation?.id || null);
    } catch (e: any) {
      if (e.message.startsWith('404:')) {
        setLatest(null);
        setViewingId(null);
      } else {
        setError(e.message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchHistory = useCallback(async (id: string) => {
    try {
      const data = await apiFetch(`/api/practitioner-cases/${id}/interpretations`);
      setHistory(data.interpretations || []);
    } catch {
      // Non-blocking: list failure shouldn't take down the panel.
      setHistory([]);
    }
  }, []);

  const fetchSpecific = useCallback(async (id: string, interpId: string) => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch(`/api/practitioner-cases/${id}/interpretations/${interpId}`);
      setLatest(data.interpretation || null);
      setViewingId(interpId);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!caseId) return;
    fetchLatest(caseId);
    fetchHistory(caseId);
  }, [caseId, fetchLatest, fetchHistory]);

  const handleGenerate = async () => {
    if (!caseId) return;
    setGenerating(true);
    setError(null);
    try {
      const data = await apiFetch(`/api/practitioner-cases/${caseId}/interpretations/generate`, {
        method: 'POST',
        body: JSON.stringify({})
      });
      // Generation may complete with status 'ok' (success) or 'partial'/'error' (still 200/207).
      const interp = data.interpretation;
      if (interp?.generationStatus === 'ok') {
        // Refresh from server to get the canonical stored shape.
        await fetchLatest(caseId);
      } else {
        setError(`Generation returned status: ${interp?.generationStatus}. ${interp?.errorMessage || ''}`);
      }
      await fetchHistory(caseId);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setGenerating(false);
    }
  };

  if (!caseId) {
    return (
      <div className="p-8 text-center text-gray-500">
        Select a case to view or generate its interpretation.
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <header className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">Naturopathic Interpretation</h2>
            <p className="text-sm text-gray-600 mt-1">
              Practitioner-facing reasoning grounded in the Plaskett College corpus.
              Built from the case history only — current logged meals and supplement data appear in the comprehensive report, not here.
            </p>
          </div>
          <button
            onClick={handleGenerate}
            disabled={generating || loading}
            className="bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap"
          >
            {generating ? 'Generating…' : latest ? '↻ Regenerate' : 'Generate'}
          </button>
        </div>
        {error && (
          <div className="mt-3 p-3 bg-red-50 border border-red-200 text-red-800 text-sm rounded">{error}</div>
        )}
      </header>

      {loading && !latest && (
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500">
          Loading…
        </div>
      )}

      {!loading && !latest && (
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500">
          <div className="text-4xl mb-3">🧪</div>
          <p className="mb-1 font-medium text-gray-700">No interpretation generated yet.</p>
          <p className="text-sm">Click <strong>Generate</strong> above to run a fresh reading from the Plaskett corpus.</p>
        </div>
      )}

      {latest && (
        <>
          {/* Metadata strip */}
          <div className="bg-white rounded-lg border border-gray-200 p-4 flex flex-wrap items-center gap-3 text-xs text-gray-600">
            <span><strong>Generated:</strong> {formatDate(latest.created_at)}</span>
            {latest.model_used && (
              <span><strong>Model:</strong> {latest.model_used}</span>
            )}
            {latest.total_tokens != null && (
              <span><strong>Tokens:</strong> {latest.total_tokens.toLocaleString()}</span>
            )}
            {latest.generation_seconds != null && Number(latest.generation_seconds) > 0 && (
              <span><strong>Time:</strong> {Number(latest.generation_seconds).toFixed(1)}s</span>
            )}
            {latest.generation_status !== 'ok' && (
              <span className="px-2 py-0.5 rounded bg-yellow-100 text-yellow-800 uppercase tracking-wide">
                {latest.generation_status}
              </span>
            )}
            {history.length > 1 && (
              <HistoryPicker
                history={history}
                viewingId={viewingId}
                onPick={(id) => fetchSpecific(caseId, id)}
              />
            )}
          </div>

          {/* Rendered interpretation */}
          <article className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <InterpretationMarkdown text={latest.interpretationText || ''} />
          </article>

          {/* Retrieval audit (collapsible) */}
          {Array.isArray(latest.qdrantSources) && latest.qdrantSources.length > 0 && (
            <details className="bg-white rounded-lg border border-gray-200 p-4">
              <summary className="cursor-pointer text-sm font-medium text-gray-700">
                Plaskett retrievals used ({latest.qdrantSources.length})
              </summary>
              <div className="mt-3 space-y-3">
                {latest.qdrantSources.map((s: any, i: number) => (
                  <div key={i} className="text-xs">
                    <p className="font-medium text-gray-700">{s.id || `Retrieval ${i + 1}`}</p>
                    <p className="text-gray-500 italic">{s.query}</p>
                    {Array.isArray(s.snippets) && s.snippets.length > 0 && (
                      <ul className="mt-1 space-y-1 list-disc pl-5">
                        {s.snippets.slice(0, 3).map((sn: any, j: number) => (
                          <li key={j}>
                            <span className="font-medium">{sn.source}</span>
                            {sn.similarity != null && <span className="text-gray-400"> ({sn.similarity})</span>}
                            {sn.excerpt && <span className="block text-gray-600">{sn.excerpt.slice(0, 240)}…</span>}
                          </li>
                        ))}
                      </ul>
                    )}
                    {s.note && <p className="text-gray-400 italic mt-1">{s.note}</p>}
                  </div>
                ))}
              </div>
            </details>
          )}
        </>
      )}
    </div>
  );
};

// ----- helpers -----

const HistoryPicker: React.FC<{
  history: InterpretationMeta[];
  viewingId: string | null;
  onPick: (id: string) => void;
}> = ({ history, viewingId, onPick }) => {
  const sorted = useMemo(
    () => [...history].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || '')),
    [history]
  );
  return (
    <label className="ml-auto flex items-center gap-2">
      <span>Run:</span>
      <select
        value={viewingId || ''}
        onChange={(e) => onPick(e.target.value)}
        className="px-2 py-1 border border-gray-300 rounded text-xs"
      >
        {sorted.map((h) => (
          <option key={h.id} value={h.id}>
            {formatDate(h.created_at)} · {h.model_used || 'unknown'} {h.generation_status !== 'ok' ? `(${h.generation_status})` : ''}
          </option>
        ))}
      </select>
    </label>
  );
};

const InterpretationMarkdown: React.FC<{ text: string }> = ({ text }) => {
  // The generator outputs five `## N. Heading` sections with paragraph text under each.
  // This is a deliberately tiny renderer — it handles only the shapes the service produces.
  const blocks = useMemo(() => parseSections(text), [text]);
  if (blocks.length === 0) {
    return <p className="text-gray-500 italic">Interpretation is empty.</p>;
  }
  return (
    <div className="prose-like text-gray-800">
      {blocks.map((b, i) => (
        <section key={i} className="mb-6 last:mb-0">
          {b.heading && (
            <h3 className="text-lg font-semibold text-gray-900 mb-2 mt-4 first:mt-0">
              {b.heading}
            </h3>
          )}
          {b.paragraphs.map((p, j) => (
            <p key={j} className="mb-3 leading-relaxed">{p}</p>
          ))}
        </section>
      ))}
    </div>
  );
};

function parseSections(md: string): Array<{ heading: string; paragraphs: string[] }> {
  if (!md) return [];
  const lines = md.split('\n');
  const blocks: Array<{ heading: string; paragraphs: string[] }> = [];
  let current: { heading: string; paragraphs: string[] } | null = null;
  let buffer: string[] = [];

  const flushPara = () => {
    if (!current) {
      // Pre-heading text — attach to a no-heading lead block.
      if (buffer.length === 0) return;
      if (blocks.length === 0 || blocks[0].heading !== '') {
        blocks.unshift({ heading: '', paragraphs: [] });
      }
      blocks[0].paragraphs.push(buffer.join(' ').trim());
      buffer = [];
      return;
    }
    if (buffer.length > 0) {
      current.paragraphs.push(buffer.join(' ').trim());
      buffer = [];
    }
  };

  for (const raw of lines) {
    const line = raw.replace(/\r$/, '');
    if (line.startsWith('## ')) {
      flushPara();
      if (current) blocks.push(current);
      current = { heading: line.slice(3).trim(), paragraphs: [] };
    } else if (line.trim() === '') {
      flushPara();
    } else {
      buffer.push(line.trim());
    }
  }
  flushPara();
  if (current) blocks.push(current);
  return blocks;
}

export default CaseInterpretationPanel;
